/**
 * Word-level diff for inline composer edits (accept / reject like an IDE). No dependencies: an LCS over
 * tokens (words + whitespace + punctuation), with a line-level pass first so long emails stay fast.
 */

export type DiffOp = { op: 'equal' | 'insert' | 'delete'; text: string }

function tokenize(s: string): string[] {
    return s.match(/\s+|[A-Za-z0-9À-ɏ'’]+|[^\sA-Za-z0-9À-ɏ'’]/g) ?? []
}

function lcsDiff(a: string[], b: string[]): DiffOp[] {
    const n = a.length
    const m = b.length
    // Trim common prefix/suffix first (most edits are local).
    let start = 0
    while (start < n && start < m && a[start] === b[start]) start++
    let endA = n
    let endB = m
    while (endA > start && endB > start && a[endA - 1] === b[endB - 1]) {
        endA--
        endB--
    }
    const A = a.slice(start, endA)
    const B = b.slice(start, endB)
    const ops: DiffOp[] = []
    if (start) ops.push({ op: 'equal', text: a.slice(0, start).join('') })
    if (A.length * B.length > 4_000_000) {
        // Too big for a token LCS: treat the middle as replaced.
        if (A.length) ops.push({ op: 'delete', text: A.join('') })
        if (B.length) ops.push({ op: 'insert', text: B.join('') })
    } else {
        const dp: Uint32Array[] = Array.from({ length: A.length + 1 }, () => new Uint32Array(B.length + 1))
        for (let i = A.length - 1; i >= 0; i--) {
            for (let j = B.length - 1; j >= 0; j--) {
                dp[i][j] = A[i] === B[j] ? dp[i + 1][j + 1] + 1 : Math.max(dp[i + 1][j], dp[i][j + 1])
            }
        }
        let i = 0
        let j = 0
        while (i < A.length && j < B.length) {
            if (A[i] === B[j]) {
                ops.push({ op: 'equal', text: A[i] })
                i++
                j++
            } else if (dp[i + 1][j] >= dp[i][j + 1]) {
                ops.push({ op: 'delete', text: A[i++] })
            } else {
                ops.push({ op: 'insert', text: B[j++] })
            }
        }
        while (i < A.length) ops.push({ op: 'delete', text: A[i++] })
        while (j < B.length) ops.push({ op: 'insert', text: B[j++] })
    }
    if (endA < n) ops.push({ op: 'equal', text: a.slice(endA).join('') })
    return merge(ops)
}

function merge(ops: DiffOp[]): DiffOp[] {
    const out: DiffOp[] = []
    for (const o of ops) {
        if (!o.text) continue
        const last = out[out.length - 1]
        if (last && last.op === o.op) last.text += o.text
        else out.push({ ...o })
    }
    return out
}

export function diffWords(before: string, after: string): DiffOp[] {
    return lcsDiff(tokenize(before ?? ''), tokenize(after ?? ''))
}

/** Plain text from simple composer HTML (paragraphs/line breaks), for diffing and previews. */
export function htmlToText(html: string): string {
    if (!html || !/[<&]/.test(html)) return html ?? ''
    return html
        .replace(/<br\s*\/?>/gi, '\n')
        .replace(/<\/(p|div|li|h[1-6])>/gi, '\n')
        .replace(/<[^>]+>/g, '')
        .replace(/&nbsp;/g, ' ')
        .replace(/&amp;/g, '&')
        .replace(/&lt;/g, '<')
        .replace(/&gt;/g, '>')
        .replace(/&quot;/g, '"')
        .replace(/&#39;/g, "'")
        .replace(/\n{3,}/g, '\n\n')
        .trim()
}

export function diffStats(ops: DiffOp[]): { inserted: number; deleted: number } {
    const words = (s: string) => (s.match(/\S+/g) ?? []).length
    return {
        inserted: ops.filter((o) => o.op === 'insert').reduce((n, o) => n + words(o.text), 0),
        deleted: ops.filter((o) => o.op === 'delete').reduce((n, o) => n + words(o.text), 0),
    }
}
